// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { QuestionnaireResponse } from '@medplum/fhirtypes';
import { NONE_OF_THE_ABOVE_CODE } from '../intake/intake-answer-types';

export interface Comorbidity {
  code: string;
  display: string;
}

/**
 * Extracts the selected comorbidities from a QuestionnaireResponse.
 *
 * The `diagnoses` item is a multi-select question whose answers are stored
 * as valueCoding entries. When the "Other" option is selected, the free-text
 * value from the `other-conditions` item is appended as an additional entry.
 *
 * @returns Array of selected comorbidities with their code and display label.
 *          Returns an empty array if none were selected or the item is absent.
 */
export function getComorbiditiesFromQR(qr: QuestionnaireResponse): Comorbidity[] {
  const comorbidities: Comorbidity[] = [];
  let otherText: string | undefined;

  for (const item of qr.item ?? []) {
    if (item.linkId === 'diagnoses') {
      for (const answer of item.answer ?? []) {
        const code = answer.valueCoding?.code;
        const display = answer.valueCoding?.display;
        if (code != null && code !== NONE_OF_THE_ABOVE_CODE && display != null) {
          comorbidities.push({ code, display });
        }
      }
    }
    if (item.linkId === 'other-conditions' && item.answer?.[0]?.valueString != null) {
      otherText = item.answer[0].valueString;
    }
  }

  if (otherText != null) {
    comorbidities.push({ code: 'other', display: otherText });
  }

  return comorbidities;
}
